import { MappedThunk } from "actions/action-thunk";
import * as actions from "actions/actions";
import {
    AppPlanCode,
    AppPlanInfo,
    SubscriptionType,
    appPlans,
    getPrivatePerUserBillingExplanation,
    planCodeToSubscriptionType,
    planInfoToPlanType,
    planCodeToPlanPrivacy,
    isChurning,
    PlanPrivacy,
    PlanType,
    getAppPlanInfo,
    planCodeToPlanType,
    getPlanName,
} from "common/app-plans";
import { Owner, UserData, isUserData } from "common/Database";
import { OwnerKind } from "common/Database/owner-kind";
import { UpgradeModal } from "designer/modals-v2/upgrade-modal/upgrade-modal";
import { dontAwait } from "common/Support";

import { useState, useEffect, useCallback, useMemo, ReactElement, useRef } from "react";
import { connect } from "react-redux";
import { OrganizationsState, RootState, getAppOwner } from "store/types";

import AppPlanPanel from "./app-settings-plan";
import { ChurnSurveyModal } from "./churn-survey-modal";
import { CardPreview } from "common/firebase-function-types";
import { trackEvent } from "analytics";
import { useConcurrentAsyncDebounced } from "hooks/use-concurrent-debounced-async";
import { getEstimateAndCardPreview } from "./app-settings-plan-lib";
import { SimpleModalContextProps } from "designer/modals-v2/modal-provider-v2/modal-context";
import { useShallowMemo } from "hooks/use-shallow-memo";

interface Props {
    readonly appID: string;
    readonly owner: Owner;
    readonly currentPlan: AppPlanCode | "legacy";
    readonly currentUserCount: number;
    readonly upgradeAppToPlan: MappedThunk<typeof actions.upgradeAppToPlan>;
    readonly isPanel: boolean;
    readonly showProOnly: boolean;
    readonly source: string;
    readonly onClosed?: () => void;
}

function AppSettingsPlanControllerInner(p: Props): ReactElement {
    const { currentPlan, currentUserCount, upgradeAppToPlan, appID, isPanel, owner, source, showProOnly, onClosed } = p;

    const [numUsers, setNumUsers] = useState<number>(currentUserCount ?? 20);
    const defaultPlan: AppPlanInfo = useMemo(
        () => ({
            ...appPlans[currentPlan],
            numberOfUsers: numUsers,
        }),
        [currentPlan, numUsers]
    );
    const [appPlanInfo, setAppPlanInfo] = useState<AppPlanInfo>(defaultPlan);

    const [privacy, setPrivacy] = useState<PlanPrivacy>(planCodeToPlanPrivacy(appPlanInfo.code));
    const [plan, setPlan] = useState<PlanType | undefined>(planInfoToPlanType(appPlanInfo));
    const [subscription, setSubscription] = useState<SubscriptionType>(planCodeToSubscriptionType(appPlanInfo.code));
    const [estimate, setEstimate] = useState<number>();

    const skipFirst = useRef(false);
    useEffect(() => {
        // skip the first as our states are already initialized appropriately
        if (skipFirst.current === false) {
            skipFirst.current = true;
            return;
        }
        if (currentPlan === "legacy") return;

        setPrivacy(planCodeToPlanPrivacy(currentPlan));
        setPlan(planCodeToPlanType(currentPlan));
        setSubscription(planCodeToSubscriptionType(currentPlan));
    }, [currentPlan]);

    const [showConfirmFromPanel, setShowConfirmFromPanel] = useState(false);
    const [upgradeComplete, setUpgradeComplete] = useState(false);

    // NOTE: If the card is undefined, card preview card is pending load from backend
    // Look at card states in app-settings-plan-lib.ts
    const [cardPreview, setCardPreview] = useState<CardPreview>();
    const [applyingNewPlan, setApplyingNewPlan] = useState<boolean>(false);

    // Note: I don't like this but we need a way to mark the args to `useConcurrentAsyncDebounced` as stale for
    // the 2 following scenarios:
    // 1. Card is updated
    // 2. Promo code is applied
    // The reason for this is because the changes to the backend happen deeper in the component and they
    // need a refreshed view of the estimate and card info.
    const [stalePreviewCount, setStalePreviewCount] = useState(0);

    const [showDowngradeModal, setShowDowngradeModal] = useState(false);
    const [showCanceledModal, setShowCanceledModal] = useState(false);

    const [estimateAndCardPreviewArgs, setEstimateAndCardPreviewArgs] = useState({
        ownerID: owner.id,
        ownerKind: owner.kind,
        appID,
        appPlanCode: appPlanInfo.code,
        numUsers,
        stalePreviewCount,
    });

    const stableEstimateAndCardPreviewArgs = useShallowMemo(estimateAndCardPreviewArgs);

    // Note: each time the args are updated, this will kick off a new debounce async request
    // for a new estimate and card info
    const estimateAndCardPreview = useConcurrentAsyncDebounced(
        getEstimateAndCardPreview,
        stableEstimateAndCardPreviewArgs,
        500
    );

    useEffect(() => {
        if (estimateAndCardPreview === null) return;
        setEstimate(estimateAndCardPreview.newEstimate);
        setCardPreview(estimateAndCardPreview.newCardPreview);
    }, [estimateAndCardPreview]);

    // NOTE: The estimate and card info are recalculated in the following case
    // - appID changes (via useEffect)
    // - plan code changes (via useEffect)
    // - numUsers changed (via useEffect)
    // - card is updated (via prop callback)
    // - promo code is applied (via prop callback)
    const recalculateEstimateAndCardPreview = useCallback(() => {
        // Clear estimate costs
        setEstimate(undefined);
        setCardPreview(undefined);

        setEstimateAndCardPreviewArgs({
            ownerID: owner.id,
            ownerKind: owner.kind,
            appID,
            appPlanCode: appPlanInfo.code,
            numUsers,
            stalePreviewCount,
        });
    }, [appID, appPlanInfo.code, numUsers, owner.id, owner.kind, stalePreviewCount]);

    const setStaleAndRecalcuate = useCallback(() => {
        setStalePreviewCount(stalePreviewCount + 1);
        recalculateEstimateAndCardPreview();
    }, [recalculateEstimateAndCardPreview, stalePreviewCount]);

    useEffect(() => {
        if (plan === undefined) return;

        const newPlan = getAppPlanInfo(plan, privacy, subscription, Math.max(20, numUsers));
        setAppPlanInfo(newPlan);

        // Recalculate estimates costs
        recalculateEstimateAndCardPreview();
    }, [privacy, plan, subscription, numUsers, appID, recalculateEstimateAndCardPreview]);

    const canApplyCancel = appPlanInfo.code !== currentPlan || (appPlanInfo.isPrivate && numUsers !== currentUserCount);

    const onCancel = useCallback(() => {
        if (currentPlan === "legacy") {
            setAppPlanInfo(appPlans[currentPlan]);
            setPlan(undefined);
        } else {
            setPrivacy(planCodeToPlanPrivacy(currentPlan));
            setPlan(planInfoToPlanType(appPlans[currentPlan]));
            setSubscription(planCodeToSubscriptionType(currentPlan));
            setNumUsers(currentUserCount);
        }
    }, [currentPlan, currentUserCount]);

    const paymentRequestInfo = useMemo(() => {
        return {
            estimateDollars: estimate ?? 0,
            label: getPlanName(appPlanInfo.code).long,
        };
    }, [appPlanInfo, estimate]);

    const onComplete = useCallback(() => {
        const fn = async () => {
            setApplyingNewPlan(true);

            trackEvent("upgrade start", { app_id: appID, intent: source, plan: appPlanInfo.code });

            const upgraded = await upgradeAppToPlan(appID, appPlanInfo.code, numUsers, paymentRequestInfo);

            setApplyingNewPlan(false);

            // Close Modals
            if (upgraded) {
                setUpgradeComplete(true);
            } else {
                onClosed?.();
            }
        };
        dontAwait(fn());
    }, [appID, source, appPlanInfo.code, upgradeAppToPlan, numUsers, paymentRequestInfo, onClosed]);

    const onSubmitDowngradeChurnSurvey = useCallback(async () => {
        await upgradeAppToPlan(appID, appPlanInfo.code, numUsers, paymentRequestInfo);
    }, [appID, appPlanInfo.code, numUsers, paymentRequestInfo, upgradeAppToPlan]);

    const onDowngradeChurnModalClosed = useCallback(() => {
        setShowDowngradeModal(false);
    }, []);

    const onSubmitCancelPlanChurnSurvey = useCallback(async () => {
        await upgradeAppToPlan(appID, AppPlanCode.Free, 0, paymentRequestInfo);
        setPrivacy(planCodeToPlanPrivacy(AppPlanCode.Free));
        setPlan(planInfoToPlanType(appPlans[AppPlanCode.Free]));
        setSubscription(planCodeToSubscriptionType(AppPlanCode.Free));
        setNumUsers(20);
    }, [appID, paymentRequestInfo, upgradeAppToPlan]);

    const onShowCanceledModal = useCallback(() => {
        setShowCanceledModal(true);
    }, []);

    const onCanceledModalClosed = useCallback(() => {
        setShowCanceledModal(false);
    }, []);

    const onDismissUnifiedConfirm = useCallback(() => {
        setShowConfirmFromPanel(false);
    }, []);

    const onApplyPanel = useCallback(() => {
        setUpgradeComplete(false);
        const currentPlanType = planInfoToPlanType(defaultPlan);
        const newPlanType = planInfoToPlanType(appPlanInfo);

        if (isChurning(currentPlanType, newPlanType)) {
            setShowDowngradeModal(true);
        } else {
            setShowConfirmFromPanel(true);
        }
    }, [defaultPlan, appPlanInfo]);

    const unifiedModal = useMemo(() => {
        if (!showConfirmFromPanel) return null;

        return (
            <UpgradeModal
                upgradeComplete={upgradeComplete}
                applyingNewPlan={applyingNewPlan}
                appPlanInfo={appPlanInfo}
                plan={plan}
                privacy={privacy}
                subscription={subscription}
                numPrivateUsers={numUsers}
                numPrivateUsersChange={setNumUsers}
                planChange={setPlan}
                privacyChange={setPrivacy}
                subscriptionChange={setSubscription}
                estimate={estimate}
                onClosed={onDismissUnifiedConfirm}
                onCompleted={onComplete}
                cardPreview={cardPreview}
                owner={owner}
                fromPanel={true}
                showProOnly={showProOnly}
                onCardUpdated={setStaleAndRecalcuate}
                onPromoCodeApplied={setStaleAndRecalcuate}
            />
        );
    }, [
        showConfirmFromPanel,
        upgradeComplete,
        applyingNewPlan,
        appPlanInfo,
        plan,
        privacy,
        subscription,
        numUsers,
        estimate,
        onDismissUnifiedConfirm,
        onComplete,
        cardPreview,
        owner,
        showProOnly,
        setStaleAndRecalcuate,
    ]);

    let privateUserDescription: string | undefined;
    if (appPlanInfo.code !== AppPlanCode.Free && appPlanInfo.isPrivate) {
        privateUserDescription = getPrivatePerUserBillingExplanation(appPlanInfo, numUsers);
    }

    const ui =
        isPanel === true ? (
            <AppPlanPanel
                appPlanInfo={appPlanInfo}
                subscription={subscription}
                plan={plan}
                privacy={privacy}
                numPrivateUsers={numUsers}
                planChange={setPlan}
                privacyChange={setPrivacy}
                subscriptionChange={setSubscription}
                numPrivateUsersChange={setNumUsers}
                canApply={canApplyCancel}
                canCancel={canApplyCancel}
                canCancelPlan={currentPlan !== AppPlanCode.Free}
                onApply={onApplyPanel}
                onCancel={onCancel}
                onCancelPlan={onShowCanceledModal}
                privateUserDescription={privateUserDescription}
            />
        ) : (
            // Need to wire this up again
            <UpgradeModal
                upgradeComplete={upgradeComplete}
                fromPanel={false}
                applyingNewPlan={applyingNewPlan}
                appPlanInfo={appPlanInfo}
                plan={plan}
                privacy={privacy}
                subscription={subscription}
                numPrivateUsers={numUsers}
                numPrivateUsersChange={setNumUsers}
                planChange={setPlan}
                privacyChange={setPrivacy}
                subscriptionChange={setSubscription}
                estimate={estimate}
                onClosed={onClosed !== undefined ? onClosed : () => undefined}
                onCompleted={onComplete}
                cardPreview={cardPreview}
                owner={owner}
                showProOnly={showProOnly}
                onCardUpdated={setStaleAndRecalcuate}
                onPromoCodeApplied={setStaleAndRecalcuate}
            />
        );

    return (
        <>
            {ui}
            {unifiedModal}
            {showDowngradeModal && (
                <ChurnSurveyModal
                    appID={appID}
                    onSubmit={onSubmitDowngradeChurnSurvey}
                    onClose={onDowngradeChurnModalClosed}
                />
            )}
            {showCanceledModal && (
                <ChurnSurveyModal
                    appID={appID}
                    onSubmit={onSubmitCancelPlanChurnSurvey}
                    onClose={onCanceledModalClosed}
                />
            )}
        </>
    );
}

interface WrapperProps {
    readonly appID: string;
    readonly userData: UserData | undefined;
    readonly upgradeAppToPlan: MappedThunk<typeof actions.upgradeAppToPlan>;
    readonly isPanel: boolean;
    readonly orgs: OrganizationsState;
    readonly showProOnly: boolean;
    readonly source: string;
    readonly onClosed?: () => void;
}

function AppSettingsPlanControllerImpl(p: WrapperProps): ReactElement | null {
    const { appID, userData, upgradeAppToPlan, isPanel, orgs, showProOnly, source, onClosed } = p;
    if (userData === undefined) {
        return null;
    }

    const owner = getAppOwner(appID, userData, orgs);

    if (owner === undefined) return null;

    const isLegacyPro = owner.kind === OwnerKind.User && owner.planAppIDs.pro.includes(appID);
    const monthlySubOwner = owner.monthlySubscription?.appPlanCodes?.find(apc => apc.appIDs.includes(appID));
    const overageSubOwner = owner.monthlySubscription?.overageItems?.find(oi => oi.appIDs.includes(appID));
    const annualSubOwner = owner.annualSubscription?.appPlanCodes?.find(apc => apc.appIDs.includes(appID));
    const unifiedPlanCode = monthlySubOwner?.code ?? annualSubOwner?.code ?? overageSubOwner?.code;
    const unifiedNumUsers = annualSubOwner?.userCount?.[appID] ?? 20;

    return (
        <AppSettingsPlanControllerInner
            owner={owner}
            appID={appID}
            currentPlan={unifiedPlanCode ?? (isLegacyPro ? "legacy" : AppPlanCode.Free)}
            currentUserCount={unifiedNumUsers}
            upgradeAppToPlan={upgradeAppToPlan}
            isPanel={isPanel}
            showProOnly={showProOnly}
            source={source}
            onClosed={onClosed}
        />
    );
}

const mapState = (rootState: RootState) => ({
    userData: isUserData(rootState.userData) ? rootState.userData : undefined,
    orgs: rootState.organizations,
});

const mapDispatch = {
    upgradeAppToPlan: actions.upgradeAppToPlan,
};

export const AppSettingsPlanController = connect(mapState, mapDispatch)(AppSettingsPlanControllerImpl);

export async function showUpgradeModal(
    modals: SimpleModalContextProps,
    source: string,
    appID: string,
    showProOnly: boolean
) {
    return modals.showModal<void>(resolve => (
        <AppSettingsPlanController
            appID={appID}
            showProOnly={showProOnly}
            source={source}
            isPanel={false}
            onClosed={() => resolve()}
        />
    ));
}
